import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { BASE_URL } from "../../Api/ApiUrls";
import FormsBottomNavbar from "./../Post Module/FormsBottomNavbar";


const JDJVPropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);

 useEffect(() => {
  const fetchProperty = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/property/${id}/`);

      // If API returns property directly
      setProperty(res.data.data || res.data);

    } catch (err) {
      console.error("Error fetching property:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchProperty();
}, [id]);

  const formatPropertyId = (id) => {
    return `PR${String(id).padStart(4, '0')}`;
  };

  const formatPrice = (amount) => {
    if (!amount || amount === 0) return "Not specified";
    
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)} L`;
    } else {
      return `₹${amount.toLocaleString()}`;
    }
  };

  const handleLocationClick = () => {
    if (!property) {
      window.alert("Property data not available");
      return;
    }
    
    // Get coordinates directly from property object (new API structure)
    const lat = property.lat;
    const lng = property.long;
    
    if (!lat || !lng) {
      window.alert("Location coordinates not available");
      return;
    }
    
    setLocationLoading(true);
    
    const openInMaps = (url) => {
      setLocationLoading(false);
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const locationString = `${lat},${lng}`;
    const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationString)}`;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${lat},${lng}&travelmode=driving`;
          openInMaps(mapsUrl);
        },
        (error) => {
          console.error("Error getting location:", error);
          openInMaps(fallbackUrl);
        },
        { timeout: 10000 }
      );
    } else {
      openInMaps(fallbackUrl);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!property) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>JD/JV Property not found</Typography>
      </Box>
    );
  }

  // Get property ID for display
  const displayId = property.property_id || property.req_id;
  
  // Get location directly from property (new API structure)
  const propertyLocation = property.location || "Not specified";

  return (
    <Box sx={{ 
      pb: 0,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        paddingLeft: '8px',
        paddingRight: '8px',
        paddingTop: '2px',
        paddingBottom: '2px'
      }}>
        <Card sx={{ 
          maxWidth: 420, 
          mx: "auto",
          mb: 2
        }}>
          {/* Header - Fixed at top of card */}
          <Box
            sx={{
              backgroundColor: "black",
              color: "#fff",
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}
          >
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={() => navigate(-1)}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>

            <Typography fontWeight={600} flexGrow={1}>
              JD/JV Property
            </Typography>

            <Box textAlign="right">
              <Typography variant="caption">
                ID: {formatPropertyId(displayId)}
              </Typography>
              <Typography variant="caption" display="block">
                Date:{" "}
                {new Date(property.created_at).toLocaleDateString("en-GB")}
              </Typography>
            </Box>
          </Box>

          {/* Scrollable Card Content */}
          <Box sx={{ 
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            paddingLeft: '8px',
            paddingRight: '8px',
            paddingTop: '2px',
            paddingBottom: '2px'
          }}>
            {/* Budget Range */}
            <Box
              sx={{
                backgroundColor: "#fff1f0",
                p: 2,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Budget Range
              </Typography>
              <Typography variant="h6" color="error" fontWeight={700}>
                {formatPrice(property.min_budget)} - {formatPrice(property.max_budget)}
              </Typography>
            </Box>

            {/* Property Info */}
            <Typography variant="body2">
              <b>Looking for:</b> {property.type ? property.type.toUpperCase() : "Not specified"}
            </Typography>

            <Typography variant="body2" mt={1}>
              <b>Property type:</b> {property.property_type || "Not specified"}
            </Typography>

<Box mt={1}>
  <Box
    onClick={handleLocationClick}
    sx={{
      cursor: "pointer",
      borderRadius: 1,
      p: 0.5,
      "&:hover": {
        backgroundColor: "#f0f0f0",
      },
    }}
  >
    {/* First Row */}
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2">
        <b>Location:</b>
      </Typography>

      {locationLoading ? (
        <CircularProgress size={16} sx={{ ml: 0.5 }} />
      ) : (
        <LocationOnIcon
          sx={{ color: "#1976d2", fontSize: "1rem", ml: 0.5 }}
        />
      )}
      
      <Typography variant="body2" sx={{ ml: 1, color: "#555" }}>
        {property.location?.split(",")[0]}
      </Typography>
    </Box>

    {/* Second Row (remaining address) */}
    <Typography
     variant="body2"
  sx={{
    color: "#555",
    lineHeight: 1.4,          // ✅ more vertical spacing
    textAlign: "justify",     // ✅ left + right align
   // ✅ better spacing between words
                     // ✅ more gap from above
    
  }}
>
      {property.location
        }
    </Typography>
  </Box>
</Box>



            {/* Additional Details Box */}
            <Box
              sx={{
                backgroundColor: "#f0f7ff",
                p: 2,
                borderRadius: 2,
                mt: 2,
              }}
            >
              <InfoRow 
                label="Minimum Budget" 
                value={formatPrice(property.min_budget)} 
              />
              <InfoRow 
                label="Maximum Budget" 
                value={formatPrice(property.max_budget)} 
              />
              {property.min_acres > 0 && (
                <InfoRow 
                  label="Minimum Acres" 
                  value={`${property.min_acres} acres`} 
                />
              )}

        {property.ratio && (
                <InfoRow 
                  label="JV/JD Ratio" 
                  value={property.ratio} 
                />
              )}

            {property.length > 0 && (
  <InfoRow
    label="Length"
    value={`${property.length} ft`}
  />
)}

{property.width > 0 && (
  <InfoRow
    label="Width"
    value={`${property.width} ft`}
  />
)}

              {property.max_acres > 0 && (
                <InfoRow 
                  label="Maximum Acres" 
                  value={`${property.max_acres} acres`} 
                />
              )}
              
              {/* New fields from the API */}
              {property.posted_by && (
                <InfoRow 
                  label="Posted By" 
                  value={property.posted_by} 
                />
              )}
              
              {(property.roadwidth > 0 || property.road_width > 0) && (
                <InfoRow 
                  label="Road Width" 
                  value={`${property.roadwidth || property.road_width} ft`} 
                />
              )}
                          {/* <InfoRow
  label="Units"
  value={property.units || "Not specified"}
/> */}
              
              {property.facing && (
                <InfoRow 
                  label="Facing" 
                  value={property.facing} 
                />
              )}
             {property.facing && (
  <InfoRow
    label="Date"
    value={
      property.created_at
        ? new Date(property.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : "-"
    }
  />
)}

            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Description/Comment Section - FIXED */}
            {/* {(property.description || property.comment) && (
              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 2,
                  mt: 2,
                  p: 2,
                  minHeight: 80,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {property.description ? "Description" : "Additional Comments"}:
                </Typography>
                <Typography variant="body2">
                  {property.description || property.comment}
                </Typography>
              </Box>
            )} */}
          </Box>
        </Card>
      </Box>

      {/* Fixed Bottom Navbar */}
      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

/* Reusable Row */
const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export default JDJVPropertyDetail;